import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral } from "app/themes/colors";
import CallPatientNumberButton from "components/suno-dialer/call-patient/call-patient-number-button.component";
import { SHOW_CONTACT_CALL_BUTTON } from "constants/feature-flags";
import { CALL_DIRECTION_INCOMING } from "constants/suno-comms";
import { CallLog } from "services/suno-comms/types/call-log.type";
import { formatPhoneNumber } from "utils/common";
import { formatCallLog } from "utils/suno-comms.util";

import CallDirection from "../call-logs/components/call-direction.component";
import CallStatus from "../call-logs/components/call-status.component";
import { getContactFullName } from "../utils";
import Recording from "./components/recording.component";
import Transcription from "./components/transcription.component";
import { useCallLogDrawer } from "./use-call-log-drawer";

export default function CallLogContent() {
  const flags = useFlags();
  const isCallButtonEnabled = flags[SHOW_CONTACT_CALL_BUTTON];

  const { callLog: data } = useCallLogDrawer();

  const theme = useTheme();
  const callLog = useMemo(() => formatCallLog(data as CallLog), [data]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    const handleTimeUpdate = () => {
      if (!callLog?.transcriptionObject) return;

      const currentTime = audio?.currentTime || 0;
      const currentSentenceIndex =
        callLog.transcriptionObject.segments.findIndex(
          (segment) =>
            currentTime >= segment.start && currentTime <= segment.end
        );
      if (currentSentenceIndex !== -1) {
        setCurrentIndex(currentSentenceIndex);
      }
    };

    audio?.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      audio?.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioRef, callLog.transcriptionObject]);

  const fullName = getContactFullName(callLog?.contact);
  const isIncomingCall = callLog?.direction === CALL_DIRECTION_INCOMING;

  return (
    <>
      <Box pl={3} pr={1}>
        <Typography variant="h5" component="div" sx={{ pb: 2 }}>
          Call Log
        </Typography>
      </Box>

      <Stack
        spacing={1}
        pt={0.5}
        pb={1}
        sx={{
          boxSizing: "border-box",
          width: "100%",
          background: misc.mutedPurple,
          boxShadow: theme.shadows[3]
        }}
      >
        <Stack pl={3} pr={1} pt={1}>
          <Typography sx={{ color: "grey" }}>Contact:</Typography>
          <Typography sx={{ fontSize: "20px", mt: 1 }}>
            {fullName || "Unknown Caller"}
          </Typography>
        </Stack>

        <Grid container pl={3} pr={1}>
          <Grid item xs={6}>
            <Stack>
              <Typography
                component="div"
                sx={{
                  color: "grey",
                  mt: 2,
                  mb: isCallButtonEnabled ? -0.5 : 0.8
                }}
              >
                Contact Phone:
              </Typography>

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ fontSize: "14px" }}>
                  {callLog.external_number
                    ? formatPhoneNumber(callLog.external_number)
                    : "Unknown number"}
                </Typography>

                {isCallButtonEnabled && (
                  <CallPatientNumberButton
                    title={fullName}
                    phoneNumber={callLog.external_number || ""}
                    size="small"
                  />
                )}
              </Stack>
            </Stack>
          </Grid>
          {!isIncomingCall && (
            <Grid item xs={6}>
              <Stack>
                <Typography
                  component="div"
                  sx={{ color: "grey", mt: 2, pb: 0.75 }}
                >
                  Clinic Phone Number:
                </Typography>

                <Typography sx={{ fontSize: "14px" }}>
                  {formatPhoneNumber(callLog.internal_number || "")}
                </Typography>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Stack>

      <Grid container pl={3} pr={1} pt={0.5}>
        <Grid item xs={4}>
          <Typography component="div" sx={{ color: "grey", mt: 2, mb: 1 }}>
            Direction:
          </Typography>

          <CallDirection direction={String(callLog?.direction)} />
        </Grid>
        <Grid item xs={4}>
          <Typography component="div" sx={{ color: "grey", mt: 2, mb: 1 }}>
            Status:
          </Typography>

          <CallStatus status={String(callLog?.status)} />
        </Grid>
        <Grid item xs={4}>
          <Typography component="div" sx={{ color: "grey", mt: 2, mb: 1 }}>
            Duration:
          </Typography>

          <Typography sx={{ fontSize: "14px", mt: 1 }}>
            {callLog.duration}
          </Typography>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 3,
          mb: 2,
          height: "2px",
          width: "100%",
          background: neutral[300]
        }}
      ></Box>
      {callLog.recording_url && (
        <Recording url={callLog.recording_url} audioRef={audioRef} />
      )}
      {callLog.transcription && (
        <Transcription
          transcription={callLog.transcription}
          transcriptionObject={callLog.transcriptionObject}
          currentIndex={currentIndex}
        />
      )}
    </>
  );
}
